import { useState } from 'react';
import api from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Star, Loader2, MessageSquare } from 'lucide-react';
import { formatLocalDate, formatTimeLocal } from '../lib/utils';
import { toast } from 'sonner';

export default function SessionRatingModal({ session, open, onClose, onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // V26 (founder) — ratings below 6/10 must come with a short note (≥ 10 chars)
  const COMMENT_REQUIRED_BELOW = 6;
  const COMMENT_MIN_CHARS = 10;
  const commentRequired = rating > 0 && rating < COMMENT_REQUIRED_BELOW;
  const commentTooShort = commentRequired && comment.trim().length < COMMENT_MIN_CHARS;

  const handleSubmit = async () => {
    if (rating === 0) { toast.error('Please select a rating'); return; }
    if (commentTooShort) { toast.error('Please tell us in a few words what could have been better.'); return; }
    setSubmitting(true);
    try {
      await api.post('/api/feedback', {
        session_id: session.session_id || session.id,
        batch_id: session.batch_id,
        instructor_id: session.instructor_id,
        rating,
        comments: comment.trim(),
        batch_name: session.batch_name,
        scheduled_date: session.scheduled_date,
      });
      toast.success('Thank you for your feedback!');
      onSubmitted?.();
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((typeof d === 'object' ? d?.message : d) || 'Failed to submit feedback');
    }
    setSubmitting(false);
  };

  const getRatingLabel = (r) => {
    if (r <= 2) return 'Poor';
    if (r <= 4) return 'Below Average';
    if (r <= 5) return 'Average';
    if (r <= 7) return 'Good';
    if (r <= 8) return 'Very Good';
    if (r <= 9) return 'Excellent';
    return 'Outstanding';
  };

  const getRatingColor = (r) => {
    if (r <= 3) return 'text-rose-500';
    if (r <= 5) return 'text-amber-500';
    if (r <= 7) return 'text-teal-500';
    return 'text-emerald-500';
  };

  const activeRating = hoveredRating || rating;

  if (!session) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="session-rating-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Rate Your Session</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Session Info */}
          <div className="p-3 rounded-xl border bg-muted/30">
            <p className="text-sm font-medium">{session.batch_name}</p>
            <p className="text-xs text-muted-foreground">
              with {session.instructor_name} &middot; {formatLocalDate(session.scheduled_date, session.scheduled_time)} &middot; {formatTimeLocal(session.scheduled_time, session.scheduled_date)}
            </p>
          </div>

          {/* 1-10 Rating */}
          <div>
            <p className="text-sm font-medium mb-3">How was your experience?</p>
            <div className="flex items-center justify-center gap-1" data-testid="rating-stars-container">
              {[1,2,3,4,5,6,7,8,9,10].map(i => (
                <button
                  key={i}
                  type="button"
                  className="p-0.5 transition-transform hover:scale-110 focus:outline-none"
                  onMouseEnter={() => setHoveredRating(i)}
                  onMouseLeave={() => setHoveredRating(0)}
                  onClick={() => setRating(i)}
                  data-testid={`rating-star-${i}`}
                >
                  <Star
                    className={`h-7 w-7 transition-colors ${
                      i <= activeRating
                        ? `${getRatingColor(activeRating)} fill-current`
                        : 'text-slate-200'
                    }`}
                  />
                </button>
              ))}
            </div>
            {activeRating > 0 && (
              <p className={`text-center text-sm font-medium mt-2 ${getRatingColor(activeRating)}`} data-testid="rating-label">
                {activeRating}/10 &middot; {getRatingLabel(activeRating)}
              </p>
            )}
          </div>

          {/* Comment */}
          <div>
            <label className="text-sm font-medium mb-1.5 flex items-center gap-1.5">
              <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
              Comments{' '}
              <span className={`text-xs font-normal ${commentRequired ? 'text-rose-600' : 'text-muted-foreground'}`} data-testid="rating-comment-required-tag">
                {commentRequired ? '(required)' : '(optional)'}
              </span>
            </label>
            {commentRequired && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2" data-testid="rating-comment-required-note">
                Thank you for your honest rating. Since this class didn&apos;t fully meet your expectations, please tell us in a few
                words what could have been better — it goes straight to our team and helps us improve your next class.
              </p>
            )}
            <Textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder={commentRequired ? 'What could we have done better?' : 'Share what you liked or what could be improved...'}
              rows={3}
              className={`resize-none ${commentTooShort ? 'border-rose-300 focus-visible:ring-rose-300' : ''}`}
              data-testid="rating-comment-input"
            />
            {commentRequired && (
              <p className="text-[11px] text-muted-foreground mt-1" data-testid="rating-comment-counter">
                {Math.min(comment.trim().length, COMMENT_MIN_CHARS)}/{COMMENT_MIN_CHARS} characters minimum
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="rating-skip-btn">Skip</Button>
          <Button onClick={handleSubmit} disabled={submitting || rating === 0 || commentTooShort} data-testid="rating-submit-btn">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Submit Rating
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
